import { useAuthFetch } from "@/hooks/data/useAuthFetch";
import { parseResponse } from "@/lib/query-helper";
import {
    useMutation,
    UseMutationOptions,
    useQueryClient,
    useQuery,
    UseQueryOptions,
} from "@tanstack/react-query";
import { APIResponse, PaginatedResponse, SearchFilter } from "@simplerealtybits/srb-types";
import { Team } from "@t/Team";
import { TeamCreateDto } from "@t/TeamCreateDto";
import { TeamUpdateDto } from "@t/TeamUpdateDto";
import { TeamGetDto } from "@t/TeamGetDto";
import { teamsQueryKeys } from "@/hooks/data/v2/query-keys";

export function useGetTeamsByFilter(filter: SearchFilter) {
    const authFetch = useAuthFetch();

    const getTeamsByFilter = async (): Promise<PaginatedResponse<Team>> => {
        return await authFetch("/api/v1/teams/getByFilter", {
            method: "POST",
            body: JSON.stringify(filter),
        }).then((response) => response.json());
    };

    const getTeamsByFilterQueryOptions = () => ({
        queryKey: teamsQueryKeys.getByFilter(filter),
        queryFn: () => getTeamsByFilter(),
        enabled: !!filter,
        select: (data: PaginatedResponse<Team>) => data.data,
    });

    return useQuery(getTeamsByFilterQueryOptions());
}

type UseCreateTeamMutationOptions = Partial<
    UseMutationOptions<APIResponse<Team>, Error, TeamCreateDto, unknown>
>;

export const useCreateTeamMutation = (options?: UseCreateTeamMutationOptions) => {
    const authFetch = useAuthFetch();
    const queryClient = useQueryClient();

    const mutationFn = async (team: TeamCreateDto): Promise<APIResponse<Team>> => {
        const response = await authFetch("/api/v1/teams/create", {
            method: "POST",
            body: JSON.stringify(team),
        });

        return await parseResponse<Team>(response, "Failed to create team");
    };

    return useMutation({
        ...options,
        mutationFn,
        onSuccess: (data, variables, onMutateResult, context) => {
            queryClient.invalidateQueries({ queryKey: ["teams"] });

            options?.onSuccess?.(data, variables, onMutateResult, context);
        },
    });
};

type UseUpdateTeamMutationOptions = Partial<
    UseMutationOptions<APIResponse<Team>, Error, TeamUpdateDto, unknown>
>;

export const useUpdateTeamMutation = (options?: UseUpdateTeamMutationOptions) => {
    const authFetch = useAuthFetch();
    const queryClient = useQueryClient();

    const mutationFn = async (team: TeamUpdateDto): Promise<APIResponse<Team>> => {
        const response = await authFetch("/api/v1/teams/update", {
            method: "POST",
            body: JSON.stringify(team),
        });

        return await parseResponse<Team>(response, "Failed to update team");
    };

    return useMutation({
        ...options,
        mutationFn,
        onSuccess: (data, variables, onMutateResult, context) => {
            queryClient.invalidateQueries({ queryKey: ["teams"] });

            options?.onSuccess?.(data, variables, onMutateResult, context);
        },
    });
};

type UseDeleteTeamMutationOptions = Partial<
    UseMutationOptions<APIResponse<Team>, Error, number, unknown>
>;

export const useDeleteTeamMutation = (options?: UseDeleteTeamMutationOptions) => {
    const authFetch = useAuthFetch();
    const queryClient = useQueryClient();

    const mutationFn = async (id: number): Promise<APIResponse<Team>> => {
        const response = await authFetch(`/api/v1/teams/${id}`, {
            method: "DELETE",
        });

        return await parseResponse<Team>(response, "Failed to delete team");
    };

    return useMutation({
        ...options,
        mutationFn,
        onSuccess: (data, variables, onMutateResult, context) => {
            queryClient.invalidateQueries({ queryKey: ["teams"] });

            options?.onSuccess?.(data, variables, onMutateResult, context);
        },
    });
};

type UseGetTeamByIdQueryOptions = Partial<UseQueryOptions<TeamGetDto, Error>>;

export const useGetTeamByIdQuery = (teamId: number, options?: UseGetTeamByIdQueryOptions) => {
    const authFetch = useAuthFetch();

    const queryFn = async (): Promise<TeamGetDto> => {
        if (!teamId || teamId === 0) {
            return Promise.resolve({} as TeamGetDto);
        }

        const response = await authFetch(`/api/v1/teams/${teamId}`, {
            method: "GET",
        });

        if (!response.ok) {
            throw new Error("Failed to fetch team");
        }

        const result: APIResponse<TeamGetDto> = await response.json();

        if (!result.success) {
            throw new Error(result.message || "Failed to fetch team");
        }

        return result.data!;
    };

    return useQuery({
        queryKey: ["teams", teamId],
        queryFn,
        enabled: teamId > 0,
        ...options,
    });
};
